import React, { ReactElement, useId } from 'react';
import { formatTranslation } from 'intl';
import { WorkflowTranslationKeys, workflowStageIconKey } from 'intl/keys';
import Box from '@mui/material/Box';
import Stack from '@mui/material/Stack';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import Typography from '@mui/material/Typography';
import { useIntl } from 'react-intl';
import { WorkflowStageIcon } from 'shared/domain/workflow/types';
import { StageIcon } from '../StageIcon';
import { WORKFLOW_STAGE_ICONS } from './constants';
import { iconSx, menuPaperSx, compactIconPickerSelectSx, compactIconPickerMenuItemSx, numberingPreviewSx } from './styles';

const NUMBERING_VALUE = '__numbering__';

type StandardStageIconPickerProps = {
  value: WorkflowStageIcon | null;
  onChange: (next: WorkflowStageIcon | null) => void;
  stageNumber: number;
  disabled?: boolean;
  fullWidth?: boolean;
};

export function StandardStageIconPicker({
  value,
  onChange,
  stageNumber,
  disabled = false,
  fullWidth = true,
}: StandardStageIconPickerProps): ReactElement {
  const intl = useIntl();
  const labelId = useId();

  const handleChange = (event: SelectChangeEvent<string>): void => {
    const selected = event.target.value;
    if (selected === NUMBERING_VALUE) {
      onChange(null);
    } else {
      onChange(selected as WorkflowStageIcon);
    }
  };

  const selectValue = value === null ? NUMBERING_VALUE : value;
  const isNumbering = value === null;

  return (
    <Box sx={fullWidth ? undefined : { width: '100%', maxWidth: 76 }}>
      <FormControl fullWidth disabled={disabled} size="small">
        <InputLabel id={labelId} {...(!fullWidth ? { shrink: true } : {})}>
          {formatTranslation(intl, WorkflowTranslationKeys.configurationStageIconLabel,)}
        </InputLabel>
        <Select
          labelId={labelId}
          label={formatTranslation(intl, WorkflowTranslationKeys.configurationStageIconLabel,)}
          value={selectValue}
          onChange={handleChange}
          data-qa="workflow-stage-icon-select"
          sx={fullWidth ? undefined : compactIconPickerSelectSx}
          renderValue={() => (
            <Stack direction="row" alignItems="center" justifyContent="center" sx={{ width: '100%' }}>
              {isNumbering ? (
                <Typography sx={{ ...numberingPreviewSx, ...(disabled ? { color: 'action.disabled' } : {}) }}>
                  {stageNumber}
                </Typography>
              ) : (
                <StageIcon
                  stageIcon={value}
                  sx={{
                    ...iconSx,
                    ...(disabled ? { color: 'action.disabled' } : {}),
                  }}
                />
              )}
            </Stack>
          )}
          MenuProps={{
            disableScrollLock: true,
            PaperProps: { sx: menuPaperSx },
          }}
        >
          <MenuItem
            value={NUMBERING_VALUE}
            data-qa="workflow-stage-icon-option-numbering"
            aria-label={formatTranslation(intl, WorkflowTranslationKeys.stageIconOrderNumber)}
            sx={compactIconPickerMenuItemSx}
          >
            <Typography sx={numberingPreviewSx}>{stageNumber}</Typography>
          </MenuItem>
          {WORKFLOW_STAGE_ICONS.map((ic) => (
            <MenuItem
              key={ic}
              value={ic}
              data-qa={`workflow-stage-icon-option-${ic}`}
              aria-label={formatTranslation(intl, workflowStageIconKey(ic))}
              sx={compactIconPickerMenuItemSx}
            >
              <StageIcon stageIcon={ic} sx={iconSx} />
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Box>
  );
}
